import React, { useCallback, useEffect, useState } from 'react';
import { Box, Text, WarningLabel } from '@common-fe/common-fe';

import { StatusWrapper } from '@/components';
import { WarnModal } from '@/components/elements';
import { PlanCardAction } from '@/modules/plan/plan.types';
import PlanModalWrapper from '@/modules/plan/PlansList/PlanModalWrapper';

import { useForceOpenModalStore } from '../stores';

import Footer from './Footer';
import PlanName from './HealthPlanName';
import PlanYear from './HealthPlanYearPicker';
import { useCreateHealthPlan } from './hooks';
// import { useCheckPaymentOrderQuery } from '../../queries';

interface Props {
  hide?: boolean;
}

const CreateHealthPlanModal: React.FC<Props> = ({ hide }) => {
  const modalStore = useForceOpenModalStore((state) => state.state);
  const handleClearModalStore = useForceOpenModalStore((state) => state.clear);
  const [modalVisible, setModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  // const handleCheck = useCheckPaymentOrderQuery();
  const handleCancel = useCallback(() => {
    setModalVisible(false);
    handleClearModalStore();
  }, [handleClearModalStore]);

  useEffect(() => {
    if (modalStore.actionType === PlanCardAction.createPlanFromTemplate && modalStore.id) {
      setModalVisible(true);
    }
  }, [modalStore.actionType, modalStore.id]);
  const {
    handleSave,
    submitted,
    loading,
    setLoading,
    confirm,
    handlePreSubmit,
    handleReset,
    planCreationError,
    setPlanCreationError,
    // planTemplate,
    // definitionId,
  } = useCreateHealthPlan(handleCancel);
  useEffect(() => {
    if (!modalVisible && !paymentModalVisible) {
      handleReset();
    }
  }, [handleReset, modalVisible, paymentModalVisible]);
  const handleSubmit = useCallback(async () => {
    const isSuccess = handlePreSubmit();
    if (isSuccess) {
      try {
        await handleSave();
      } catch {
        setLoading(false);
        setPaymentModalVisible(false);
        setModalVisible(true);
      }
      // try {
      //   await handleCheck({
      //     templateId: planTemplate || '',
      //     definitionId: definitionId || '',
      //   });
      //   try {
      //     await handleSave();
      //   } catch {
      //     setLoading(false);
      //     setPaymentModalVisible(false);
      //     setModalVisible(true);
      //   }
      // } catch {
      //   setLoading(false);
      //   setPaymentModalVisible(true);
      //   // setModalVisible(false);
      // }
    }
  }, [handlePreSubmit, handleSave, setLoading]);

  const handleTogglePaymentVisible = useCallback((value: boolean) => {
    if (!value) {
      setModalVisible(true);
    }
    setPaymentModalVisible(value);
  }, []);
  const handleToggleModal = useCallback(() => {
    const newValue = !modalVisible;
    setModalVisible(newValue);
    handleReset();
    if (!newValue) {
      handleClearModalStore();
    }
  }, [handleClearModalStore, handleReset, modalVisible]);
  return (
    <>
      <PlanModalWrapper
        visible={modalVisible}
        testId="CreateHealthPlan"
        onSetVisible={handleToggleModal}
        hide={hide}
        buttonText="Create Health Plan"
        minButtonSize={230}
        modalTitle="Create New Health Plan"
      >
        <WarnModal
          testId="CreateHealthPlan_payment_order"
          isPendingIcon
          visible={paymentModalVisible}
          paddingless
          onSetVisible={handleTogglePaymentVisible}
          header="plan template you selected is not included in any payment order or processing definition"
          helptext={`In that case, manual claims will not be available for your employees enrolled in this plan.
        Do you want to proceed?`}
          buttonText="Close"
          onSubmit={handleSave}
          submitButtonText="Proceed"
        />
        <WarnModal
          visible={planCreationError}
          testId="CreateHealthPlan_server_error"
          onSetVisible={setPlanCreationError}
          header="The server encountered an error processing the request"
          helptext={`It doesn't appear to have affected your data, but we cannot save the plan with the latest records.
        Our technical staff have been automatically notified and will be looking into this with the utmost urgency.`}
          buttonText="Close"
          onSubmit={handleSave}
          submitButtonText="Try Again"
        />

        <Box>
          <Box dir="column" background="module" pad="spacing24" round="moduleRound">
            <PlanName testId="CreateHealthPlan" planTemplateId={modalStore.id} />
            <PlanYear testId="CreateHealthPlan" />

            <Box margin={{ top: 'spacing24' }}>
              <StatusWrapper status="pending">
                <Box
                  margin="spacing12"
                  pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
                  background={{ color: 'warningContainer' }}
                  border={{ size: '1px', color: 'warningBorder' }}
                  round="fieldRound"
                >
                  <Text weight="bold" margin={{ bottom: 'spacing8' }} color="textBody">
                    <WarningLabel data-testid="CreateHealthPlan_warning_message">
                      Does everything look good? Please double check your selections.
                    </WarningLabel>
                  </Text>

                  <Box>
                    <Text
                      data-testid="CreateHealthPlan_confirm_message"
                      margin={{ bottom: 'spacing8' }}
                      color="textBody"
                      size="14px"
                    >
                      Once created, you can’t change the health plan template or plan year.
                    </Text>
                  </Box>
                </Box>
              </StatusWrapper>
            </Box>
          </Box>
          <Footer
            testId="CreateHealthPlan_footer"
            unconfirmed={submitted && !confirm}
            loading={loading}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </Box>
      </PlanModalWrapper>
    </>
  );
};
export default CreateHealthPlanModal;
